import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { concatMap, finalize, retry, shareReplay } from 'rxjs/operators';

const API_PATH = '/multilocationanalytics.v1.AnalyticsService/QueryMetrics';
const THROTTLE_LIMIT = 20;

@Injectable()
export class QueryMetricsThrottleInterceptor implements HttpInterceptor {
  private activeRequests = 0;
  private readonly delayedRequestTriggers: Subject<void>[] = [];
  private readonly requests: { [key: string]: Observable<HttpEvent<unknown>> } = {};

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.endsWith(API_PATH)) {
      return next.handle(req);
    }

    // return from cache the response of a query metric
    const requestBody = JSON.stringify(req.body);
    if (this.requests[requestBody]) {
      return this.requests[requestBody];
    }

    // store to cache the query metrics response handling
    this.requests[requestBody] = next.handle(req).pipe(
      retry(3),
      finalize(() => this.onRequestFinalize()),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

    this.activeRequests++;
    if (this.activeRequests <= THROTTLE_LIMIT) {
      return this.requests[requestBody];
    }

    const trigger = new Subject<void>();
    this.delayedRequestTriggers.push(trigger);
    return trigger.pipe(concatMap(() => this.requests[requestBody]));
  }

  private onRequestFinalize(): void {
    this.activeRequests--;
    if (this.delayedRequestTriggers.length) {
      const firstTrigger = this.delayedRequestTriggers.shift();
      firstTrigger.next();
      firstTrigger.complete();
    }
  }
}
